import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Megaphone, Send, Loader2, Paperclip, X, Users, ShieldCheck, Search, CalendarClock, Save, Trash2, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const BulkEmail = () => {
  const [mode, setMode] = useState('all'); // all | department | selected
  const [departments, setDepartments] = useState([]);
  const [totalWithEmail, setTotalWithEmail] = useState(0);
  const [department, setDepartment] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  // Saved templates
  const [templates, setTemplates] = useState([]);
  // Scheduling
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledList, setScheduledList] = useState([]);

  const loadTemplates = () => {
    axios.get(`${API}/api/document-email/templates`)
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  };

  const loadScheduled = () => {
    axios.get(`${API}/api/document-email/scheduled`)
      .then((r) => setScheduledList(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/api/document-email/departments`)
      .then((r) => { setDepartments(r.data?.departments || []); setTotalWithEmail(r.data?.total_active_with_email || 0); })
      .catch(() => {});
    axios.get(`${API}/api/employees`)
      .then((r) => setEmployees((r.data || []).filter((e) => e.is_active && e.email)))
      .catch(() => {});
    loadTemplates();
    loadScheduled();
  }, []);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject || '');
    setMessage(t.message || '');
    toast({ title: 'Template applied', description: t.name });
  };

  const saveTemplate = async () => {
    if (!subject.trim() && !message.trim()) {
      toast({ title: 'Nothing to save', description: 'Add a subject or message first.', variant: 'destructive' });
      return;
    }
    const name = window.prompt('Save this message as a template. Template name:');
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/api/document-email/templates`, { name: name.trim(), subject, message });
      toast({ title: 'Template saved', description: name.trim() });
      loadTemplates();
    } catch (err) {
      toast({ title: 'Could not save template', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await axios.delete(`${API}/api/document-email/templates/${id}`);
      loadTemplates();
    } catch {
      toast({ title: 'Could not delete template', variant: 'destructive' });
    }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm('Cancel this scheduled email?')) return;
    try {
      await axios.delete(`${API}/api/document-email/scheduled/${id}`);
      toast({ title: 'Scheduled email cancelled' });
      loadScheduled();
    } catch (err) {
      toast({ title: 'Could not cancel', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const recipientCount = useMemo(() => {
    if (mode === 'all') return totalWithEmail;
    if (mode === 'department') return departments.find((d) => d.name === department)?.count || 0;
    return selectedIds.length;
  }, [mode, totalWithEmail, departments, department, selectedIds]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.name || '').toLowerCase().includes(q) || (e.employee_code || '').toLowerCase().includes(q)
    );
  }, [employees, search]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const canSend = subject.trim() && message.trim() && recipientCount > 0 &&
    (mode !== 'department' || department) && (mode !== 'selected' || selectedIds.length > 0);

  const handleSend = async () => {
    if (!canSend) return;
    let scheduledIso = null;
    if (scheduleOn) {
      if (!scheduledAt) {
        toast({ title: 'Pick a date & time', description: 'Choose when to send, or turn off scheduling.', variant: 'destructive' });
        return;
      }
      const when = new Date(scheduledAt);
      if (isNaN(when.getTime()) || when.getTime() <= Date.now() + 30000) {
        toast({ title: 'Invalid time', description: 'Scheduled time must be at least a minute in the future.', variant: 'destructive' });
        return;
      }
      scheduledIso = when.toISOString();
    }
    const verb = scheduledIso
      ? `Schedule this email for ${recipientCount} staff at ${new Date(scheduledAt).toLocaleString()}?`
      : `Send this email to ${recipientCount} staff? Everyone is BCC'd so addresses stay private.`;
    if (!window.confirm(verb)) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('subject', subject);
      fd.append('message', message);
      fd.append('recipient_mode', mode);
      if (mode === 'department') fd.append('department', department);
      if (mode === 'selected') fd.append('employee_ids', JSON.stringify(selectedIds));
      if (scheduledIso) fd.append('scheduled_at', scheduledIso);
      if (file) fd.append('attachment', file);
      const res = await axios.post(`${API}/api/document-email/bulk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: scheduledIso ? 'Bulk email scheduled' : 'Bulk email sent', description: res.data?.message || 'Done.' });
      setSubject(''); setMessage(''); setFile(null); setSelectedIds([]);
      setScheduleOn(false); setScheduledAt('');
      if (scheduledIso) loadScheduled();
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const pendingScheduled = scheduledList.filter((s) => s.status === 'scheduled');

  return (
    <div className="space-y-4 max-w-3xl" data-testid="bulk-email-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Megaphone className="h-6 w-6 text-indigo-600" /> Bulk Email
        </h1>
        <p className="text-gray-600 mt-1 flex items-center gap-1.5 text-sm">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          Everyone is added as BCC — recipients never see each other's email addresses.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[['all', 'All active staff'], ['department', 'By department'], ['selected', 'Pick individuals']].map(([val, label]) => (
              <Button
                key={val}
                type="button"
                variant={mode === val ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode(val)}
                data-testid={`bulk-mode-${val}`}
              >
                {label}
              </Button>
            ))}
          </div>

          {mode === 'department' && (
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="w-full sm:w-72" data-testid="bulk-department-select">
                <SelectValue placeholder="Choose a department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d) => (
                  <SelectItem key={d.name} value={d.name}>{d.name} ({d.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {mode === 'selected' && (
            <div className="border rounded-md">
              <div className="p-2 border-b flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  className="w-full text-sm outline-none"
                  placeholder="Search name or code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="bulk-employee-search"
                />
              </div>
              <div className="max-h-56 overflow-y-auto divide-y">
                {filteredEmployees.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(e.id)}
                      onChange={() => toggleSelect(e.id)}
                      data-testid={`bulk-select-${e.id}`}
                    />
                    <span className="font-medium">{e.name}</span>
                    <span className="text-gray-400 text-xs">{e.employee_code} · {e.department}</span>
                  </label>
                ))}
                {filteredEmployees.length === 0 && (
                  <p className="p-3 text-sm text-gray-400">No staff match your search.</p>
                )}
              </div>
            </div>
          )}

          <Badge variant="outline" className="text-sm" data-testid="bulk-recipient-count">
            <Users className="h-3.5 w-3.5 mr-1" /> {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base">Message</CardTitle>
            <div className="flex items-center gap-2">
              {templates.length > 0 && (
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="bulk-template-select">
                    <FileText className="h-3.5 w-3.5 mr-1 text-indigo-500" />
                    <SelectValue placeholder="Load template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between pr-1">
                        <SelectItem value={t.id} className="flex-1">{t.name}</SelectItem>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteTemplate(t.id, t.name); }}
                          className="text-rose-500 hover:text-rose-700 px-1"
                          data-testid={`bulk-template-delete-${t.id}`}
                          title="Delete template"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button type="button" variant="outline" size="sm" onClick={saveTemplate} data-testid="bulk-save-template-btn">
                <Save className="h-4 w-4 mr-1" /> Save as template
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="bulk-subject">Subject</Label>
            <Input id="bulk-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} data-testid="bulk-subject" />
          </div>
          <div>
            <Label htmlFor="bulk-message">Message</Label>
            <Textarea id="bulk-message" rows={8} value={message} onChange={(e) => setMessage(e.target.value)} disabled={sending} data-testid="bulk-message" />
          </div>
          <div>
            <Label>Attachment (optional)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                id="bulk-file"
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-file-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('bulk-file').click()} disabled={sending}>
                <Paperclip className="h-4 w-4 mr-1" /> Choose file
              </Button>
              {file && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  {file.name}
                  <button type="button" onClick={() => setFile(null)} className="text-rose-500"><X className="h-3.5 w-3.5" /></button>
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-amber-600" /> Schedule for later
            </CardTitle>
            <Switch checked={scheduleOn} onCheckedChange={setScheduleOn} data-testid="bulk-schedule-toggle" />
          </div>
        </CardHeader>
        {scheduleOn && (
          <CardContent className="space-y-2">
            <Label htmlFor="bulk-schedule-at">Send date &amp; time (your local time)</Label>
            <Input
              id="bulk-schedule-at"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full sm:w-64"
              data-testid="bulk-schedule-at"
            />
            <p className="text-xs text-gray-500">The email will be queued and sent automatically at this time.</p>
          </CardContent>
        )}
      </Card>

      {/* Queued scheduled emails */}
      {pendingScheduled.length > 0 && (
        <Card data-testid="bulk-scheduled-list">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Upcoming scheduled emails ({pendingScheduled.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingScheduled.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 p-2 border rounded-md text-sm" data-testid={`scheduled-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{s.subject}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(s.scheduled_at).toLocaleString()} · {s.recipient_count} recipient{s.recipient_count === 1 ? '' : 's'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50 shrink-0"
                  onClick={() => cancelScheduled(s.id)}
                  data-testid={`scheduled-cancel-${s.id}`}
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSend} disabled={!canSend || sending} size="lg" data-testid="bulk-send-btn">
          {sending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {scheduleOn ? 'Scheduling…' : 'Sending…'}</>
          ) : scheduleOn ? (
            <><CalendarClock className="h-4 w-4 mr-2" /> Schedule for {recipientCount} (BCC)</>
          ) : (
            <><Send className="h-4 w-4 mr-2" /> Send to {recipientCount} (BCC)</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default BulkEmail;
